import { inject, Injectable } from '@angular/core'
import { jwtDecode } from 'jwt-decode'
import { AccountName } from '@monorepo/golab-generated'
import { Actor } from '@monorepo/next-lab'
import { Observable, of } from 'rxjs'
import { MonorepoUser, UserGolab, UserPlayer, UserTranslation } from '../models/user.model'
import { AUTH_CONFIG, AuthEnvironmentModel } from '../models/auth-environment.model'

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private authEnvironmentConfig = inject<AuthEnvironmentModel>(AUTH_CONFIG)

  private LOCAL_STORAGE_KEY = 'access_token'
  private _accessToken: string | null = null

  constructor() {
    this.LOCAL_STORAGE_KEY = this.authEnvironmentConfig.accessTokenKey || this.LOCAL_STORAGE_KEY
    if (this.authEnvironmentConfig.useLocalStorage) {
      const storedToken = localStorage.getItem(this.authEnvironmentConfig.accessTokenKey!)
      if (storedToken) {
        this._accessToken = storedToken
      }
    }
  }

  public getAccessToken(): string | null {
    return this._accessToken
  }

  public saveAccessToken(token: string | null): void {
    this._accessToken = token

    if (this.authEnvironmentConfig.useLocalStorage) {
      if (token) {
        localStorage.setItem(this.LOCAL_STORAGE_KEY, token)
      } else {
        localStorage.removeItem(this.LOCAL_STORAGE_KEY)
      }
    }
  }

  public signOut(): void {
    this._accessToken = null

    if (this.authEnvironmentConfig.useLocalStorage) {
      localStorage.removeItem(this.LOCAL_STORAGE_KEY)
    }
  }

  public getUser(): UserGolab | UserTranslation | UserPlayer | null {
    return this.getAppUser<UserGolab | UserTranslation | UserPlayer>()
  }

  public getUserObservable(): Observable<MonorepoUser | null> {
    return of(this.getUser())
  }

  public getActor(): Actor {
    const user = this.getUser() as UserGolab | UserPlayer
    if (!user) throw new Error('No user found')
    if (!user.sub) throw new Error('User has no id')

    if ('nickname' in user) {
      return {
        id: user.sub,
        displayName: user.nickname,
        objectType: 'graasp_student',
      }
    } else {
      if (!user.username) throw new Error('User has no name')
      return {
        id: user.sub,
        displayName: user.username,
        objectType: 'graasp_editor',
      }
    }
  }

  public getAccountName(): AccountName {
    const user = this.getUser() as UserGolab
    if (!user) throw new Error('No user found')
    if (!user.username) throw new Error('User has no name')

    return {
      _id: user.sub,
      username: user.username,
    }
  }

  public isPreviewUser(): boolean {
    const user = this.getUser() as UserGolab
    return user?.roles === 'Preview'
  }

  private decodeToken<T>(token: string | null): T | null {
    if (!token) return null
    try {
      const decodedToken = jwtDecode<T>(token)

      if ((decodedToken as any).sub === 'fake-user-id') {
        return null
      }

      return decodedToken
    } catch (e) {
      console.error('Error decoding token', e)
      return null
    }
  }

  private getAppUser<T>(): T | null {
    return this.decodeToken<T>(this._accessToken)
  }
}
